import React, { useEffect, useState, useMemo } from 'react';
import { useTranslations, useLocale } from 'use-intl';
import { X, Save, Copy, ChevronUp, ChevronDown } from 'react-feather';
import { useCustomerVariablesStore } from '@/pages/SupportClients/useCustomerVariables';
import { CustomerProcessVariable } from '@/pages/CustomerSupport/types';
import Button from '@/stories/Library/Components/Button/Button';
import InputField from '@/stories/Library/Components/InputField/InputField';
import MoneyInputField from '@/stories/Library/Components/MoneyInputField/MoneyInputField';
import TextArea from '@/stories/Library/Components/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { TabItem, TabMenu } from '@/UIlibrary/TabMenu/TabMenu';
import { getLanguageFromPath } from '@/utils/utils';
import { Tooltip } from '@/stories/Library/Components/Tooltip/Tooltip';
import Modal from '@/stories/Library/Components/Modal/Modal';
import { Roles } from '@/pages/ClientProfiles/Roles.types';
import { useSupabaseAuth } from '@/pages/CustomerSupport/useSupabaseAuth';
import { useOrganizationId } from '@/pages/CustomerSupport/useOrganizationId';
import OfferingVehiclesSection from './OfferingVehiclesSection';
import RiskNotificationPhonesSection from './RiskNotificationPhonesSection';

const ManageOrganization: React.FC = () => {
	const t = useTranslations('SupportClients');
	const tLayout = useTranslations('Layout');
	const locale = useLocale();
	const language = getLanguageFromPath(window.location.pathname) || locale;
	const { isLoading: authLoading } = useSupabaseAuth(Roles.KOGNI_ADMIN);
	const {
		variables,
		isLoading: isLoadingVariables,
		loadVariables,
		insertVariable,
		error: variablesError
	} = useCustomerVariablesStore();

	const { organizationId, isLoading: isLoadingOrgId } = useOrganizationId(authLoading);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [historicalSortField, setHistoricalSortField] = useState<
		'variable_name' | 'variable_value' | 'created_at' | 'created_by'
	>('created_at');
	const [historicalSortDirection, setHistoricalSortDirection] = useState<'asc' | 'desc'>('desc');
	const [newVariable, setNewVariable] = useState<Partial<CustomerProcessVariable>>({
		variable_name: '',
		variable_value: null,
		text_value: null,
		description: '',
		variable_type: 'numeric'
	});

	const { currentVariables, historicalVariables } = useMemo(() => {
		const current: CustomerProcessVariable[] = [];
		const historical: CustomerProcessVariable[] = [];
		const latestByVariableName = new Map<string, CustomerProcessVariable>();

		variables.forEach((variable) => {
			const existing = latestByVariableName.get(variable.variable_name);

			if (!existing) {
				latestByVariableName.set(variable.variable_name, variable);
			} else {
				const variableEffectiveDate = new Date(variable.effective_from);
				const existingEffectiveDate = new Date(existing.effective_from);
				const variableEffectiveTime = variableEffectiveDate.getTime();
				const existingEffectiveTime = existingEffectiveDate.getTime();

				const variableCreatedTime = new Date(variable.created_at).getTime();
				const existingCreatedTime = new Date(existing.created_at).getTime();

				const getComparisonTime = (effectiveTime: number, createdTime: number) => {
					const effectiveDate = new Date(effectiveTime);
					const hours = effectiveDate.getUTCHours();
					const minutes = effectiveDate.getUTCMinutes();
					const seconds = effectiveDate.getUTCSeconds();
					const isMidnight = hours === 0 && minutes === 0 && seconds === 0;
					return isMidnight ? createdTime : effectiveTime;
				};

				const variableComparisonTime = getComparisonTime(
					variableEffectiveTime,
					variableCreatedTime
				);
				const existingComparisonTime = getComparisonTime(
					existingEffectiveTime,
					existingCreatedTime
				);

				if (variableComparisonTime > existingComparisonTime) {
					latestByVariableName.set(variable.variable_name, variable);
				} else if (
					variableComparisonTime === existingComparisonTime &&
					variableCreatedTime > existingCreatedTime
				) {
					latestByVariableName.set(variable.variable_name, variable);
				}
			}
		});

		const currentVariableIds = new Set(Array.from(latestByVariableName.values()).map((v) => v.id));

		variables.forEach((variable) => {
			if (currentVariableIds.has(variable.id)) {
				current.push(variable);
			} else {
				historical.push(variable);
			}
		});

		current.sort((a, b) => a.variable_name.localeCompare(b.variable_name));

		return { currentVariables: current, historicalVariables: historical };
	}, [variables]);

	const sortedHistoricalVariables = useMemo(() => {
		const sorted = [...historicalVariables];

		sorted.sort((a, b) => {
			let comparison = 0;

			switch (historicalSortField) {
				case 'variable_name':
					comparison = a.variable_name.localeCompare(b.variable_name);
					break;
				case 'variable_value':
					comparison = (a.variable_value || 0) - (b.variable_value || 0);
					break;
				case 'created_at':
					comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
					break;
				case 'created_by':
					comparison = (a.created_by || '').localeCompare(b.created_by || '');
					break;
			}

			return historicalSortDirection === 'asc' ? comparison : -comparison;
		});

		return sorted;
	}, [historicalVariables, historicalSortField, historicalSortDirection]);

	const handleHistoricalSort = (
		field: 'variable_name' | 'variable_value' | 'created_at' | 'created_by'
	) => {
		if (historicalSortField === field) {
			setHistoricalSortDirection(historicalSortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setHistoricalSortField(field);
			setHistoricalSortDirection('desc');
		}
	};

	const renderSortIcon = (
		field: 'variable_name' | 'variable_value' | 'created_at' | 'created_by'
	) => {
		if (historicalSortField !== field) return null;
		return historicalSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
	};

	useEffect(() => {
		if (organizationId) {
			loadVariables(organizationId);
		}
	}, [organizationId, loadVariables]);

	const formatMoneyValueForInput = (value: number): string => {
		return value.toString();
	};

	const parseMoneyValueFromInput = (value: string | null): number => {
		if (!value) return 0;
		const cleaned = value.replace(/[^\d.]/g, '');
		const parsed = parseFloat(cleaned);
		return isNaN(parsed) ? 0 : parsed;
	};

	const handleCreateFromVariable = (variable: CustomerProcessVariable) => {
		setNewVariable({
			variable_name: variable.variable_name,
			variable_value: variable.variable_value,
			text_value: variable.text_value,
			description: variable.description || '',
			variable_type: variable.variable_type
		});
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setNewVariable({
			variable_name: '',
			variable_value: null,
			text_value: null,
			description: '',
			variable_type: 'numeric'
		});
	};

	const handleCancelAddVariable = () => {
		handleCloseModal();
	};

	const handleSaveVariable = async () => {
		if (!organizationId || !newVariable.variable_name) {
			return;
		}

		if (newVariable.variable_type === 'text' && !newVariable.text_value) {
			return;
		}

		if (newVariable.variable_type === 'numeric' && newVariable.variable_value === null) {
			return;
		}

		const success = await insertVariable(organizationId, {
			variable_name: newVariable.variable_name,
			variable_value:
				newVariable.variable_type === 'numeric' ? (newVariable.variable_value as number) : null,
			text_value: newVariable.variable_type === 'text' ? newVariable.text_value || null : null,
			description: newVariable.description || null,
			variable_type: newVariable.variable_type as 'text' | 'numeric'
		});

		if (success) {
			handleCloseModal();
		}
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return 'N/A';
		const localeCode = language === 'es' ? 'es-MX' : 'en-US';
		return new Date(dateString).toLocaleDateString(localeCode, {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const formatVariableValue = (variable: CustomerProcessVariable): string => {
		if (variable.variable_type === 'text') {
			return variable.text_value || '-';
		}

		if (variable.variable_value === null) {
			return '-';
		}

		const moneyKeywords = [
			'money',
			'amount',
			'price',
			'cost',
			'payment',
			'fee',
			'salary',
			'income',
			'budget',
			'revenue',
			'charge',
			'consumption',
			'expense',
			'expenditure',
			'spending',
			'value',
			'total',
			'subtotal',
			'discount',
			'tax',
			'commission'
		];
		const lowerName = variable.variable_name.toLowerCase();
		const isMoney = moneyKeywords.some((keyword) => lowerName.includes(keyword));

		if (isMoney) {
			return new Intl.NumberFormat('es-MX', {
				style: 'currency',
				currency: 'MXN',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(variable.variable_value);
		}
		return variable.variable_value.toString();
	};

	const renderProcessVariablesContent = () => (
		<div className="bg-white rounded-lg border border-gray-200 p-6">
			{isLoadingVariables ? (
				<p className="text-gray-500 text-center py-6">{t('LoadingVariables')}</p>
			) : variables.length === 0 ? (
				<p className="text-gray-500 text-center py-6">{t('NoVariables')}</p>
			) : (
				<>
					{currentVariables.length > 0 && (
						<div className="mb-8">
							<h3 className="text-md font-semibold mb-4 text-gray-800">{t('CurrentVariables')}</h3>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b border-gray-200">
											<th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
												{t('VariableName')}
											</th>
											<th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
												{t('VariableValue')}
											</th>
											<th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
												{t('VariableDescription')}
											</th>
											<th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
												{t('CreatedAt')}
											</th>
											<th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
												{t('Actions')}
											</th>
										</tr>
									</thead>
									<tbody>
										{currentVariables.map((variable) => (
											<tr key={variable.id} className="border-b border-gray-100 hover:bg-gray-50">
												<td className="py-3 px-4 text-sm font-medium">{variable.variable_name}</td>
												<td className="py-3 px-4 text-sm">{formatVariableValue(variable)}</td>
												<td className="py-3 px-4 text-sm text-gray-600">
													{variable.description || '-'}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{formatDate(variable.created_at)}
												</td>
												<td className="py-3 px-4 text-sm">
													<Tooltip content={t('CreateNewVariableFromThis')}>
														<Button
															label=""
															onClick={() => handleCreateFromVariable(variable)}
															startIcon={<Copy size={14} />}
															color="secondary"
															size="small"
															textOnly
														/>
													</Tooltip>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

					{historicalVariables.length > 0 && (
						<div>
							<h3 className="text-md font-semibold mb-4 text-gray-600">
								{t('HistoricalVariables')}
							</h3>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b border-gray-200">
											<th
												className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
												onClick={() => handleHistoricalSort('variable_name')}
											>
												<div className="flex items-center gap-1">
													{t('VariableName')}
													{renderSortIcon('variable_name')}
												</div>
											</th>
											<th
												className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
												onClick={() => handleHistoricalSort('variable_value')}
											>
												<div className="flex items-center gap-1">
													{t('VariableValue')}
													{renderSortIcon('variable_value')}
												</div>
											</th>
											<th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
												{t('VariableDescription')}
											</th>
											<th
												className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
												onClick={() => handleHistoricalSort('created_at')}
											>
												<div className="flex items-center gap-1">
													{t('CreatedAt')}
													{renderSortIcon('created_at')}
												</div>
											</th>
											<th
												className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
												onClick={() => handleHistoricalSort('created_by')}
											>
												<div className="flex items-center gap-1">
													{t('CreatedBy')}
													{renderSortIcon('created_by')}
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										{sortedHistoricalVariables.map((variable) => (
											<tr key={variable.id} className="border-b border-gray-100 hover:bg-gray-50">
												<td className="py-3 px-4 text-sm font-medium">{variable.variable_name}</td>
												<td className="py-3 px-4 text-sm">{formatVariableValue(variable)}</td>
												<td className="py-3 px-4 text-sm text-gray-600">
													{variable.description || '-'}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{formatDate(variable.created_at)}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{variable.created_by || '-'}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);

	const tabs: TabItem[] = [
		{ title: t('ProcessVariables'), content: renderProcessVariablesContent() },
		{
			title: t('OfferingVehicles'),
			content: <OfferingVehiclesSection organizationId={organizationId} embeddedInTab />
		},
		{
			title: t('RiskNotificationPhones'),
			content: <RiskNotificationPhonesSection organizationId={organizationId} embeddedInTab />
		}
	];

	if (authLoading || isLoadingVariables || isLoadingOrgId) {
		return (
			<div className="p-3 md:p-8">
				<div className="mb-6">
					<Typography type="display" size="md">
						{tLayout('ManageOrganization')}
					</Typography>
				</div>
			</div>
		);
	}

	return (
		<div className="p-3 md:p-8">
			<div className="mb-6">
				<Typography type="display" size="md">
					{tLayout('ManageOrganization')}
				</Typography>
			</div>

			<div className="flex flex-col gap-6">
				<TabMenu tabs={tabs} maxWidth={1500} />
			</div>

			<Modal isOpen={isModalOpen} closeable onClose={handleCloseModal} maxWidth={600}>
				<div className="flex flex-col gap-6">
					<Typography type="display" size="sm">
						{t('CreateNewVariable')}
					</Typography>
					<InputField
						label={t('VariableName')}
						type="text"
						value={newVariable.variable_name || ''}
						onChange={(e) => setNewVariable((prev) => ({ ...prev, variable_name: e.target.value }))}
						size="default"
						disabled
					/>
					{newVariable.variable_type === 'numeric' ? (
						<MoneyInputField
							label={t('VariableValue')}
							value={formatMoneyValueForInput(newVariable.variable_value || 0)}
							onChange={(value) =>
								setNewVariable((prev) => ({
									...prev,
									variable_value: parseMoneyValueFromInput(value)
								}))
							}
							size="default"
						/>
					) : (
						<InputField
							label={t('VariableValue')}
							type="text"
							value={newVariable.text_value || ''}
							onChange={(e) => setNewVariable((prev) => ({ ...prev, text_value: e.target.value }))}
							size="default"
						/>
					)}
					<TextArea
						label={t('Description')}
						value={newVariable.description || ''}
						onChange={(e) => setNewVariable((prev) => ({ ...prev, description: e.target.value }))}
						rows={4}
					/>
					{variablesError && <p className="text-red-500 text-sm">{variablesError}</p>}
					<div className="flex gap-2 pt-2">
						<Button
							label={t('Cancel')}
							onClick={handleCancelAddVariable}
							startIcon={<X size={16} />}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button
							label={t('Save')}
							onClick={handleSaveVariable}
							startIcon={<Save size={16} />}
							color="primary"
							size="medium"
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default ManageOrganization;
